"use client";

import { ChevronDown, X } from "lucide-react";
import { useState } from "react";
import { useMedia } from "react-use";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { EdgeFadeScroller } from "@/components/ui/edge-fade-scroller";
import {
  useNearbyLandmarks,
  type InitialLandmarkCountry,
  type PlacePoint,
  type ViewBox,
} from "@/lib/landmarks/client";

const LANDMARK_KINDS = ["sight", "station", "area", "airport"] as const;
type LandmarkKind = (typeof LANDMARK_KINDS)[number];
import type { HubTranslate } from "./hub-context";
import { FLOATING_SHADOW } from "./hub-ui";

interface LandmarkPillProps {
  /** 지금 보이는 지도 범위 — 중심으로 **나라**를 정하고, 그 나라 명소 전체를 목록에 올린다. */
  view: ViewBox;
  /**
   * 서버가 첫 화면 지도 중심으로 정해 둔 나라(10-05) — 있으면 나라 조회 없이 마운트 즉시
   * 목록을 받는다. 없으면(서버가 기준점을 모를 때) 예전처럼 지도 중심으로 나라를 묻는다.
   */
  initialCountry?: InitialLandmarkCountry | null;
  /** 적용된 명소 — 없으면 null. 명소로 검색해 들어오면 그 명소가 들어온다. */
  value: PlacePoint | null;
  onChange: (point: PlacePoint | null) => void;
  floating?: boolean;
  locale: string;
  t: HubTranslate;
}

/**
 * 명소 알약 + 피커(09-23 에어비앤비 방식 → 09-28 전 세계). 탭(명소·역·동네·공항) 안에서
 * 하나를 고른다. 목록은 **지도가 보는 나라의 명소 전체**(전 세계 명소 데이터,
 * `/api/places/by-country`, 인지도 순) — 도쿄를 보다가도 오사카 명소를 고를 수 있다.
 *
 * 알약은 목록을 기다리지 않고 첫 렌더부터 그린다(10-05) — 목록이 오기 전에 열면 시트 안에서
 * 스켈레톤을 보인다. 명소가 **확실히** 없을 때(나라를 못 정했거나 그 나라 목록이 비었을 때)만,
 * 고른 것도 없으면 알약을 그리지 않는다.
 *
 * 축 알약과 같은 규칙: 피커 안 선택은 임시이고 "필터 적용하기"를 눌러야 반영, 바깥 탭으로
 * 닫으면 버린다. sm 이상은 팝오버, 미만은 바텀 시트.
 */
export function LandmarkPill({
  view,
  initialCountry = null,
  value,
  onChange,
  floating = false,
  locale,
  t,
}: LandmarkPillProps) {
  const { lists: nearby, iso, status } = useNearbyLandmarks(view, locale, initialCountry);
  const countryName = (() => {
    if (!iso) return null;
    try {
      return new Intl.DisplayNames([locale], { type: "region" }).of(iso) ?? null;
    } catch {
      return null;
    }
  })();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<PlacePoint | null>(value);
  const tabs = LANDMARK_KINDS.filter((kind) => (nearby?.[kind]?.length ?? 0) > 0);
  // 고른 명소가 목록 어느 탭에 있는지 — id 가 같거나(전 세계 명소) 이름이 같으면(사전 지명)
  const kindOf = (p: PlacePoint | null) =>
    p ? tabs.find((k) => nearby?.[k].some((x) => x.id === p.id || x.name === p.name)) : undefined;
  // null = 자동(고른 명소의 탭, 없으면 첫 탭) — 목록이 열린 뒤에 도착해도 맞는 탭이 선다
  const [tabChoice, setTabChoice] = useState<LandmarkKind | null>(null);
  const tab: LandmarkKind =
    tabChoice && tabs.includes(tabChoice) ? tabChoice : (kindOf(value) ?? tabs[0] ?? "sight");
  const isWide = useMedia("(min-width: 640px)", false);

  const openPicker = () => {
    setDraft(value);
    setTabChoice(null);
    setOpen(true);
  };
  const apply = () => {
    onChange(draft);
    setOpen(false);
  };

  const selected = value;
  const active = !!selected;
  const pill = (
    <button
      type="button"
      onClick={openPicker}
      aria-expanded={open}
      className={cn(
        // 축 알약·공용 필터 버튼과 같은 치수(모바일 34/13, 데스크톱 40/14)
        "inline-flex h-8.5 shrink-0 items-center gap-0.5 whitespace-nowrap rounded-full bg-white pl-3.5 pr-2.5 text-[13px] font-medium text-gray-700 transition hover:bg-gray-50",
        "mapShow:h-10 mapShow:pl-4 mapShow:pr-3 mapShow:text-sm",
        floating
          ? `pointer-events-auto border border-black/10 ${FLOATING_SHADOW}`
          : "border border-gray-300",
        // 고른 명소는 진한 테두리 — 에어비앤비와 같이 "지금 이 기준으로 보는 중"
        active && "border-2 border-gray-900 font-semibold text-gray-900",
      )}
    >
      <span className="max-w-[12rem] truncate">
        {selected ? selected.name : t("hub.axisLandmark")}
      </span>
      <ChevronDown className="h-3.5 w-3.5 text-gray-400 mapShow:h-4 mapShow:w-4" />
    </button>
  );

  const list = nearby?.[tab] ?? [];
  // 목록이 오기 전에 연 경우 — 탭·목록 자리에 같은 치수의 회색 막대(허브 시트 스켈레톤과 같은 색)
  const loadingPanel = (
    <div aria-busy="true">
      <div className="flex gap-5 border-b border-gray-100 px-5 pt-3 pb-3">
        {[14, 10, 12].map((w) => (
          <span key={w} className="h-5 animate-pulse rounded bg-gray-100" style={{ width: `${w * 4}px` }} />
        ))}
      </div>
      <div className="max-h-[min(22rem,50vh)] overflow-hidden py-1">
        {[70, 55, 80, 60, 65, 50].map((w, i) => (
          <div key={i} className="flex items-center gap-3 px-5 py-2.5">
            <span className="h-5 w-5 shrink-0 animate-pulse rounded-full bg-gray-100" />
            <span className="h-4 animate-pulse rounded bg-gray-100" style={{ width: `${w}%` }} />
          </div>
        ))}
      </div>
    </div>
  );
  const panel = (
    <div className="flex flex-col">
      {/* 탭이 넘치면 가로 스크롤 — 시트 끝에서 끝까지 넘기고, 첫·마지막 탭만 px-5 안에
          선다. 더 있는 쪽 끝만 흐린다(10-01, 검색 화면 가로 줄 공통). */}
      {status === "loading" ? (
        loadingPanel
      ) : (
        <>
          <EdgeFadeScroller wrapperClassName="border-b border-gray-100">
            <div role="tablist" className="flex w-max gap-5 px-5 pt-3">
              {tabs.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  role="tab"
                  aria-selected={tab === kind}
                  onClick={() => setTabChoice(kind)}
                  className={cn(
                    "shrink-0 border-b-2 pb-3 text-sm whitespace-nowrap transition",
                    tab === kind
                      ? "border-gray-900 font-semibold text-gray-900"
                      : "border-transparent text-gray-500 hover:text-gray-800",
                  )}
                >
                  {t(`hub.landmarkKind.${kind}`)}
                </button>
              ))}
            </div>
          </EdgeFadeScroller>
          <div
            role="radiogroup"
            className="max-h-[min(22rem,50vh)] overflow-y-auto overscroll-contain py-1"
            data-vaul-no-drag
          >
            {list.map((item) => {
              const checked = !!draft && (draft.id === item.id || draft.name === item.name);
              return (
                <button
                  key={item.id}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  onClick={() => setDraft(checked ? null : item)}
                  className="flex w-full items-center gap-3 px-5 py-2.5 text-left text-sm text-gray-900 hover:bg-gray-50"
                >
                  <span
                    className={cn(
                      "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-[1.5px]",
                      checked ? "border-gray-900" : "border-gray-400",
                    )}
                  >
                    {checked ? <span className="h-2.5 w-2.5 rounded-full bg-gray-900" /> : null}
                  </span>
                  <span className="min-w-0 truncate">{item.name}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
      <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-5 py-4">
        <button
          type="button"
          onClick={() => setDraft(null)}
          disabled={!draft}
          className="text-sm text-gray-600 underline underline-offset-2 hover:text-gray-900 disabled:opacity-40"
        >
          {t("hub.clearAxis")}
        </button>
        <button
          type="button"
          onClick={apply}
          className="h-11 rounded-xl bg-orange-500 px-5 text-sm font-semibold text-white hover:bg-orange-600"
        >
          {t("hub.applyAxis")}
        </button>
      </div>
    </div>
  );

  // 명소가 확실히 없고 고른 것도 없을 때만 알약을 두지 않는다 — 아직 모르면(loading) 그린다
  if (!value && status === "none") return null;

  if (isWide) {
    return (
      <Popover open={open} onOpenChange={(next) => (next ? openPicker() : setOpen(false))}>
        <PopoverTrigger asChild>{pill}</PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={8}
          className="w-[360px] rounded-2xl border-gray-100 bg-white p-0 shadow-lg"
        >
          {panel}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <span className="contents">
      {pill}
      <Drawer open={open} onOpenChange={(next) => (next ? openPicker() : setOpen(false))}>
        <DrawerContent className="h-auto max-h-none bg-white p-0">
          {/* 모바일 시트만 제목(09-28, 에어비앤비와 같이) — 무엇을 고르는 창인지. 데스크톱
              팝오버는 알약 바로 아래라 제목 없이 탭부터. */}
          <div className="flex items-center justify-between gap-3 px-5 pt-5">
            <DrawerTitle className="min-w-0 truncate text-lg font-bold text-gray-900">
              {value
                ? t("hub.landmarkSheetTitle", { name: value.name })
                : countryName
                  ? t("hub.landmarkSheetTitleCountry", { country: countryName })
                  : t("hub.landmarkSheetTitleArea")}
            </DrawerTitle>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t("hub.close")}
              className="-mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {panel}
        </DrawerContent>
      </Drawer>
    </span>
  );
}

